import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { Calendar as CalendarIcon, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface EventFormProps {
  initialGroup?: string | null;
  initialDate?: string | null;
  customCalendars?: Array<{
    id: string;
    name: string;
    color: string;
  }>;
}

const EventForm = ({ initialGroup = null, initialDate = null, customCalendars = [] }: EventFormProps) => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [startTime, setStartTime] = useState("12:00");
  const [endTime, setEndTime] = useState("12:00");
  const [selectedGroup, setSelectedGroup] = useState(initialGroup || "my-calendar");
  const [groups, setGroups] = useState<{id: string, name: string, color: string}[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Always update selected group when initialGroup prop changes
  useEffect(() => {
    if (initialGroup) {
      console.log("Setting initial group:", initialGroup);
      setSelectedGroup(initialGroup);
    }
  }, [initialGroup]);
  
  // Update end date when start date changes
  useEffect(() => {
    if (startDate) {
      setEndDate(new Date(startDate));
    }
  }, [startDate]);
  
  // Set initial date and time when provided
  useEffect(() => {
    if (initialDate) {
      try {
        console.log("Setting initial date and time from:", initialDate);
        const date = new Date(initialDate);
        
        // Set the start date
        setStartDate(date);
        
        // Set the end date to match
        setEndDate(date);
        
        // Extract and format the time (HH:MM)
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const formattedTime = `${hours}:${minutes}`;
        
        console.log("Extracted time:", formattedTime);
        
        // Set both start and end time
        setStartTime(formattedTime);
        setEndTime(formattedTime);
      } catch (err) {
        console.error("Error parsing initialDate:", err);
      }
    }
  }, [initialDate]);
  
  useEffect(() => {
    // Fetch user groups from Supabase
    const fetchGroups = async () => {
      if (user) {
        try {
          console.log("Fetching groups for user:", user.id);
          const { data: groupData, error: groupError } = await supabase
            .from('ft_groups')
            .select('id, name, color')
            .eq('created_by', user.id);
            
          if (groupError) {
            console.error("Error fetching groups:", groupError);
            return;
          }
          
          // Fetch custom calendars
          const { data: calendarData, error: calendarError } = await supabase
            .from('ft_custom_calendars')
            .select('id, name, color')
            .eq('user_id', user.id);
            
          if (calendarError) {
            console.error("Error fetching custom calendars:", calendarError);
            return;
          }
          
          const userGroups = groupData || [];
          const userCalendars = calendarData || [];
          
          console.log("User groups:", userGroups);
          console.log("User custom calendars:", userCalendars);
          
          // Combine standard groups with custom calendars
          const allGroups = [...userGroups, ...userCalendars];
          setGroups(allGroups);
          
          // If initialGroup is provided but not a valid ID, try to find it by name
          if (initialGroup && !allGroups.some(g => g.id === initialGroup)) {
            console.log("Looking for group match for:", initialGroup);
            const groupMatch = allGroups.find(g => 
              g.name.toLowerCase() === initialGroup.toLowerCase() || 
              g.name.toLowerCase() === (
                initialGroup === 'family' ? 'family' : 
                initialGroup === 'friends' ? 'friends' :
                initialGroup === 'hobby' ? 'hobby' :
                initialGroup === 'holidays' ? 'holidays' : ''
              )
            );
            
            if (groupMatch) {
              console.log("Found group match:", groupMatch);
              setSelectedGroup(groupMatch.id);
            }
          }
        } catch (err) {
          console.error("Error fetching user groups:", err);
        }
      }
    };
    
    fetchGroups();
  }, [user, initialGroup]);
  
  const combineDateAndTime = (date?: Date, time = "00:00") => {
    if (!date) return null;
    
    const [hours, minutes] = time.split(":").map(Number);
    const result = new Date(date);
    result.setHours(hours, minutes, 0);
    
    return result;
  };
  
  const onSubmit = async () => {
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Event title is required",
        variant: "destructive"
      });
      return;
    }
    
    if (!startDate || !endDate) {
      toast({
        title: "Error",
        description: "Start and end dates are required",
        variant: "destructive"
      });
      return;
    }
    
    const start = combineDateAndTime(startDate, startTime);
    const end = combineDateAndTime(endDate, endTime);
    
    if (!start || !end) {
      toast({
        title: "Error", 
        description: "Invalid date or time format",
        variant: "destructive"
      });
      return;
    }
    
    if (start >= end) {
      toast({
        title: "Error",
        description: "End time must be after start time",
        variant: "destructive"
      });
      return;
    }
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to create an event",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Ensure all required fields are present and properly formatted
      const eventData = {
        title: title.trim(),
        description: description.trim() || null,
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        location: location.trim() || null,
        created_by: user.id,
        group_id: selectedGroup === "my-calendar" ? null : selectedGroup, // Handle "my-calendar" value
        created_at: new Date().toISOString(), // Add created_at timestamp
        updated_at: new Date().toISOString()  // Add updated_at timestamp
      };
      
      console.log("Creating event with data:", eventData);
      
      // Insert event into Supabase with better error handling
      const { data: newEvent, error } = await supabase
        .from('ft_events')
        .insert(eventData)
        .select();
        
      if (error) {
        console.error("Supabase error creating event:", error);
        
        // Check for specific error types and provide helpful messages
        if (error.code === "23505") {
          throw new Error("An event with this title already exists at this time");
        } else if (error.code === "23502") {
          throw new Error("Missing required fields. Please check all inputs and try again.");
        } else {
          throw error;
        }
      }
      
      console.log("Event created successfully:", newEvent);
      
      // If event was created successfully but no data returned, still proceed
      if (!newEvent || newEvent.length === 0) {
        console.warn("Event created but no data returned");
      }
      
      toast({
        title: "Event created",
        description: "Your event has been created successfully"
      });
      
      // Navigate to the calendar page
      navigate('/calendar');
    } catch (err: any) {
      console.error("Error creating event:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to create event. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteEvent = async (eventId: string) => {
    if (!user || !eventId) return;
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('ft_events')
        .delete()
        .eq('id', eventId)
        .eq('created_by', user.id);
        
      if (error) throw error;
      
      toast({
        title: "Event deleted",
        description: "Your event has been deleted successfully"
      });
      
      navigate('/calendar');
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete event",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };
  
  // Safely handle pasted content by sanitizing inputs
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Allow paste but sanitize the content
    const pastedText = e.clipboardData.getData('text');
    // Let the default paste happen but log it for debugging
    console.log("Content pasted:", pastedText.substring(0, 50) + (pastedText.length > 50 ? "..." : ""));
  };
  
  // Handle start date selection and close popover
  const handleStartDateSelect = (date: Date | undefined) => {
    setStartDate(date);
    setEndDate(date); // Set end date to match start date
    setStartDateOpen(false); // Close the popover
  };
  
  // Handle end date selection and close popover
  const handleEndDateSelect = (date: Date | undefined) => {
    setEndDate(date);
    setEndDateOpen(false); // Close the popover
  };
  
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Event Title</label>
            <Input
              placeholder="Enter event title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onPaste={handlePaste}
              className="mt-1"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <Textarea
              placeholder="Enter event description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={handlePaste}
              className="mt-1"
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <div className="flex flex-col space-y-2 mt-1">
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={handleStartDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <div className="flex flex-col space-y-2 mt-1">
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={handleEndDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Location</label>
            <Input
              placeholder="Enter event location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onPaste={handlePaste}
              className="mt-1"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Calendar</label>
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a calendar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="my-calendar">My Calendar</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    <div className="flex items-center gap-2">
                      <Badge className={group.color || "bg-blue-100 text-blue-800"}>•</Badge>
                      <span className="text-black">{group.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex gap-4">
          <Button type="submit" disabled={isSubmitting} className="flex-1 bg-blue-600 hover:bg-blue-700">
            {isSubmitting ? "Creating..." : "Create Event"}
          </Button>
          
          {eventToDelete && (
            <Button 
              type="button" 
              variant="destructive" 
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </form>
      
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the event
              and remove it from all calendars.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => eventToDelete && handleDeleteEvent(eventToDelete)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EventForm;
